import { clarity } from 'react-microsoft-clarity';
import { ANALYTICS_PRODUCT_FRUIT_CAMPAIGNS } from 'constants/url';
import request from 'utils/request';

import { APP_STATES, CHECK_LIST_STATUS, PAYMENT_METHOD_GROUPS } from './constants';
import { IMerchantDetails, IProductFruitCampaigns } from './type';
import { Events } from './events';

const MIN_WIDTH_FOR_DESKTOP = 768;


/**
 * Creates a user info object.
 * @param {MerchantDetails} merchantDetails - Object containing merchant details.
 * @param {ProductFruitCampaigns} productFruitCampaigns - Object containing campaign details.
 * @returns {Object} userInfo - Object containing user information.
 */
export function createUserInfo(
  merchantDetails: IMerchantDetails = {},
  productFruitCampaigns: IProductFruitCampaigns = {}
) {
  const { username, key } = productFruitCampaigns;
  const {
    merchantName: firstname,
    contactPerson: lastname,
  } = merchantDetails;

  const signUpAt = new Date().toISOString(); 

  const userInfo = {
    username: username || 'Unknown',  // Default if no username is provided
    firstname,
    lastname,
    signUpAt,
    role: 'Merchant',
    props: {},
    hmac: {}
  };

  if (key) {
    userInfo.hmac = { hash: key }; 
  }
  return userInfo;
}



/**
 * Function to check if a specific value exists in the campaigns array.
 * @param {Array} campaigns - Array of campaign objects.
 * @param {string} key - The key to check (e.g., 'campaign', 'campaignId').
 * @param {any} value - The value to search for.
 * @returns {boolean} - True if the value exists, otherwise false.
 */

//identifyCampaign(campaigns, 'campaign', 'DAY_ZERO_SHOPIFY_MODAL')); 
export function identifyCampaign(campaigns,value, key ='campaign',) {
   if (!Array.isArray(campaigns) || campaigns.length === 0) {
    return false;
  }
  return campaigns?.some(campaign => campaign[key] === value) || false;
}



/**
 * Function to get the campaign ID (campaignId) by campaign name or return the first campaign's ID if no name is provided.
 * @param {Array} campaigns - Array of campaign objects.
 * @param {string} [campaignName] - Optional. The campaign name to search for.
 * @returns {number|null} - The campaign ID (campaignId) or null if not found.
 */
export function getCampaignId(campaigns, campaignName='') {
  if (!Array.isArray(campaigns) || campaigns.length === 0) {
    return null;
  }

  const campaign = campaignName
    ? campaigns.find(camp => camp.campaign === campaignName)
    : campaigns[0];  // Return the first campaign if no name is passed

  return Number(campaign?.campaignId) || null;
}


export function removeProtocol(url:string):string{
  return url?.replace(/^https?:\/\//, '');
}


/**
 * Function to get the highest priority campaign from a list of campaigns.
 * @param {Array} campaigns - Array of campaign objects.
 * @returns {object|null} - The highest priority campaign object, or null if none found.
 */
export function getHighestPriorityCampaign(campaigns) {
  if (!Array.isArray(campaigns) || campaigns.length === 0) {
    return null;
  }

  // Sort campaigns by priority (ascending) and return the first one
  return campaigns.sort((a, b) => a.priority - b.priority)[0] || null;
}



/**
 * Utility function to send analytics data.
 * @param {string} eventKey - The key for the event from the EVENTS configuration.
 * @param {string} action - The action performed (e.g., "CLICKED", "OPENED").
 * @param {object} [meta] - Optional metadata for the event.
 * @returns {void}
 */
export function sendAnalyticsData(eventKey, meta = {}) {
  try {
    const eventConfig = Events[eventKey];

    if (!eventConfig) {
      console.error(`Invalid eventKey: ${eventKey}`);
      return;
    }

    const isMobile= window.innerWidth <= MIN_WIDTH_FOR_DESKTOP


    const mergedMeta = { ...eventConfig.meta, mobile: isMobile, ...meta };

    const payload = {
      ...eventConfig,
      meta: mergedMeta,
    };
   
    const { url, method } = ANALYTICS_PRODUCT_FRUIT_CAMPAIGNS;

    const options = {
      method,
      body: JSON.stringify(payload),
    };

    request(url, options).catch((error) => {
      console.error('Failed to send analytics data:', error.response || error);
    });
    clarity?.setTag('Dayzero',eventKey)
  } catch (error) {
    console.error('Unexpected error during analytics event tracking:', error);
  }
}


/**
 * Function to reorder and filter payment method groups based on enabled modes and plugin activation status.
 * 
 * @param {string[]} enabledModes - An array of enabled payment method identifiers.
 * @param {boolean} isPluginActivated - Flag indicating if the plugin is activated.
 * @returns {Array} - Returns a reordered and filtered list of payment method groups.
 * 
 * - If the plugin is not activated:
 *   - The "Cards" payment method group is always enabled and assigned the highest priority (order = 1).
 *   - Other groups are disabled and assigned incremental order values starting from 2.
 *   - The groups are then sorted by their assigned order.
 * 
 * - If the plugin is activated:
 *   - Each group is checked to see if it has any methods present in `enabledModes`.
 *   - Groups with at least one enabled method are marked as enabled and retained in the list.
 *   - Disabled groups are filtered out from the final result.
 * 
 * The function ensures proper ordering and filtering of payment method groups based on business logic.
 */

export const getEnabledPaymentMethods = (enabledModes: string[], isPluginActivated: boolean) => {
  let modes;

  if (!isPluginActivated) {
    modes = PAYMENT_METHOD_GROUPS.map(group => {
      if (group.name === 'Cards') {
        group.enabled = true;
        group.order = 1;
      } else {
        group.enabled = false;
        group.order = 2 + PAYMENT_METHOD_GROUPS.indexOf(group);
      }
      return group;
    }).sort((a, b) => a.order - b.order);
  } else {
    modes = PAYMENT_METHOD_GROUPS.map(group => {
      const isEnabled = group.methods.some(method => enabledModes?.includes(method));
      group.enabled = isEnabled;
      return group;
    });

    modes = modes.filter(group => group.enabled);
  }

  return modes;
};


/**
 * Get unfinished checklists into finished and unfinished groups.
 * @param {Array} checklists - List of checklists
 * @returns {Object} - Separated unfinished and finished checklists
 */
export const getUnfinishedChecklists = (checklists) => {
  const unfinishedChecklists = checklists?.filter(
    checklist => checklist.state !== CHECK_LIST_STATUS.FINISHED
  );

  return unfinishedChecklists;
};


/**
 * Finds a checklist by its name from the provided list.
 * @param {Array} checklists - List of checklists
 * @param {string} checklistName - The name of the checklist to find
 * @returns {Object|null} - The matched checklist or null if not found
 */
export const findChecklistByName = (checklists, checklistName) => {
  return checklists?.find(checklist => checklist.name === checklistName) || null;
};



/**
 * Extracts the state of a checklist item by its internal ID.
 *
 * @param {Array} items - The list of checklist items.
 * @param {string} id - The internal ID of the checklist item to find.
 * @returns {string|undefined} - The state of the checklist item, or undefined if not found.
 */
export const getChecklistItemState = (items, id) => {
  return items.find((item) => item.internalId === id)?.state;
};




/**
 * Determines the app state based on the provided list of installed apps.
 *
 * @param {Array<string>|null} apps - List of installed apps or `null` if no apps are installed.
 * @returns {string} - Returns:
 *   - `'CASHFREE'` if `apps` includes `'ONE_CASHFREE_PAYMENTS_APP'`.
 *   - `'CARD'` if `apps` includes `'CARD_APP'`.
 *   - `'NO_METHOD'` if `apps` is `null`, empty, or unrecognized.
 */
export const determineAppState = (apps) => {
  if (apps === null) {
    return  APP_STATES.NO_METHOD;
  }
  if (Array.isArray(apps)) {
    if (apps.includes('ONE_CASHFREE_PAYMENTS_APP')) {
      return APP_STATES.CASHFREE;
    }
    if (apps.includes('CARD_APP')) {
      return APP_STATES.CARD;
    }
  }
  return APP_STATES.NO_METHOD;
};


/**
 * Extract all campaign names from the provided data array.
 *
 * @param {Array<Object>} dataArray - The array of campaign objects.
 * @returns {Array<string>} - An array of campaign names.
 */
export function getAllCampaignNames(dataArray) {
    if (!Array.isArray(dataArray) || dataArray.length === 0) {
        return [];
    }

    return dataArray
        .filter(item => item && typeof item.campaign === 'string')
        .map(item => item.campaign);
}



/**
 * Returns an array of internalId values for all items with state "done".
 *
 * @param {Array} items - The list of checklist items.
 * @returns {Array} - Array of internalId strings for "done" items.
 */
export const getDoneItemIds = (items) => {
  return items
    .filter((item) => item.state === CHECK_LIST_STATUS.DONE)
    .map((item) => item.internalId);
};